<template>
  <div class="rounded-md border p-4" :class="alertClasses" role="alert">
    <div class="flex space-x-3">
      <div v-if="showIcon" class="shrink-0">
        <CheckCircleIcon
          v-if="type === 'success'"
          class="size-5 text-green-400"
        />
        <XCircleIcon v-else-if="type === 'error'" class="size-5 text-red-400" />
        <FlagIcon v-else-if="type === 'flagged'" class="size-5 text-red-400" />
        <ExclamationTriangleIcon
          v-else-if="type === 'warning'"
          class="size-5 text-yellow-400"
        />
        <InformationCircleIcon v-else class="size-5 text-blue-400" />
      </div>
      <div>
        <h3 class="text-sm font-medium" :class="titleClasses">{{ title }}</h3>
        <div
          v-if="$slots.default"
          class="text-sm"
          :class="[descriptionClasses, { 'mt-1': title }]"
        >
          <slot />
        </div>
        <slot name="footer" />
      </div>
      <div v-if="$slots.button" class="flex grow items-center justify-end">
        <slot name="button" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
  FlagIcon,
} from '@heroicons/vue/24/solid';
import type { AlertTypes } from '@/types';

const props = defineProps({
  type: {
    type: String as PropType<AlertTypes>,
    default: 'information',
  },
  title: {
    type: String,
  },
  showIcon: {
    type: Boolean,
    default: true,
  },
});

const alertClasses = computed(() => {
  return {
    'bg-blue-50 border-blue-400': props.type === 'information',
    'bg-green-50 border-green-400': props.type === 'success',
    'bg-red-50 border-red-400':
      props.type === 'error' || props.type === 'flagged',
    'bg-yellow-50 border-yellow-400': props.type === 'warning',
  };
});

const titleClasses = computed(() => {
  return {
    'text-blue-800': props.type === 'information',
    'text-green-800': props.type === 'success',
    'text-red-800': props.type === 'error' || props.type === 'flagged',
    'text-yellow-800': props.type === 'warning',
  };
});

const descriptionClasses = computed(() => {
  return {
    'text-blue-700': props.type === 'information',
    'text-green-700': props.type === 'success',
    'text-red-700': props.type === 'error' || props.type === 'flagged',
    'text-yellow-700': props.type === 'warning',
  };
});
</script>
